import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import { ALL_MENU_LINKS } from '@components/sidebar/sidebar-v2';
import { usePermissions } from '@contexts/PermissionsContext';
import { useLanguage } from '@contexts/LanguageContext';

/** Matches /prefix and /prefix/... but not /prefix-something-else. */
function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Route-level counterpart of the sidebar's menu filter.
 *
 * Hiding a sidebar entry is not access control — the page still loaded for
 * anyone who typed the URL. This resolves the pathname to the most specific
 * menu entry that owns it and blocks the render unless the user's role granted
 * that exact entry, so `/doc-pabean/draft/12/edit` needs the Draft menu even
 * when Dok Pabean is granted.
 *
 * Paths owned by no menu entry (profile, one-off tools) stay reachable, and
 * legacy users are unaffected — `isFallback` short-circuits everything.
 */
export default function MenuAccessGuard({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { canAccessMenu } = usePermissions();

  const path = pathname.split('?')[0];

  // Most specific menu entry owning this path — longest matching prefix.
  const owner = ALL_MENU_LINKS.filter((link) => matchesPrefix(path, link)).sort((a, b) => b.length - a.length)[0];

  // Deliberately delegates to canAccessMenu rather than reading menuLinks
  // directly: every way of failing open (legacy users, unrestricted roles,
  // ALL/ALL_MST, the Custom Roles escape hatch) then applies to typed URLs and
  // to the sidebar identically, instead of drifting apart.
  if (!owner || canAccessMenu(owner)) return <>{children}</>;

  return <AccessDeniedPage />;
}

function AccessDeniedPage() {
  const { t } = useLanguage();

  return (
    <div className="grid min-h-[60vh] place-items-center">
      <div className="flex max-w-[420px] flex-col items-center gap-4 px-4">
        <span
          aria-hidden
          className="flex size-14 items-center justify-center rounded-full bg-surface-2 text-fg-muted ring-1 ring-line"
        >
          <ShieldOff className="size-7" />
        </span>
        <h3 className="text-center font-semibold text-base text-fg">{t('lumen.frame.accessDeniedTitle')}</h3>
        <p className="text-center text-fg-muted text-sm">{t('lumen.frame.accessDeniedBody')}</p>
      </div>
    </div>
  );
}

export { MenuAccessGuard };
